/**
 * Credential Editor
 * Credential létrehozása/szerkesztése
 */

import React, { useState, useEffect } from 'react';
import { Credential, CredentialType, CREDENTIAL_TYPES, CredentialField } from '../../types/credentials';
import { useCredentials } from '../../hooks/useCredentials';

interface CredentialEditorProps {
  credential?: Credential;
  type?: CredentialType;
  onClose: () => void;
}

const CredentialEditor: React.FC<CredentialEditorProps> = ({ credential, type, onClose }) => {
  const { create, update } = useCredentials();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const credentialType = credential?.type || type!;
  const metadata = CREDENTIAL_TYPES[credentialType];
  const isEditing = !!credential;

  // Form state
  const [formData, setFormData] = useState({
    name: credential?.name || '',
    description: credential?.description || '',
    data: credential?.data || {}
  });

  // Field érték beállítása
  const setFieldValue = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [fieldName]: value
      }
    }));
  };

  // Nested field érték beállítása (pl. from.name)
  const setNestedFieldValue = (path: string, value: any) => {
    const parts = path.split('.');
    setFormData(prev => {
      const newData = { ...prev.data };
      let current: any = newData;
      
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      
      current[parts[parts.length - 1]] = value;
      
      return {
        ...prev,
        data: newData
      };
    });
  };

  // Field érték lekérése
  const getFieldValue = (fieldName: string): any => {
    const parts = fieldName.split('.');
    let value: any = formData.data;
    
    for (const part of parts) {
      value = value?.[part];
    }
    
    return value ?? '';
  };

  // Mentés
  const handleSave = async () => {
    // Validáció
    if (!formData.name.trim()) {
      setError('A név megadása kötelező');
      return;
    }

    // Kötelező mezők ellenőrzése
    for (const field of metadata.fields) {
      if (field.required && !getFieldValue(field.name)) {
        setError(`A(z) "${field.label}" mező kitöltése kötelező`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await update(credential.id, {
          name: formData.name,
          description: formData.description,
          data: formData.data
        });
      } else {
        await create({
          name: formData.name,
          description: formData.description,
          type: credentialType,
          data: formData.data,
          isActive: true
        });
      }

      onClose();
    } catch (err: any) {
      setError(err.message || 'Hiba történt a mentés során');
    } finally {
      setLoading(false);
    }
  };

  // Field renderelése
  const renderField = (field: CredentialField) => {
    const value = getFieldValue(field.name);
    const setValue = (val: any) => {
      if (field.name.includes('.')) {
        setNestedFieldValue(field.name, val);
      } else {
        setFieldValue(field.name, val);
      }
    };

    switch (field.type) {
      case 'text':
      case 'password':
        return (
          <input
            type={field.type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(parseInt(e.target.value))}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        );

      case 'checkbox':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => setValue(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              {field.description || 'Engedélyezés'}
            </span>
          </label>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Válassz...</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ← Vissza
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {metadata.icon} {isEditing ? 'Credential szerkesztése' : `Új ${metadata.label}`}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {metadata.description}
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
        {/* Általános mezők */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Általános információk
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Név <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="pl. Gmail - Marketing"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Leírás
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Opcionális leírás..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Credential specifikus mezők */}
        {metadata.fields.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Kapcsolat beállítások
            </h3>

            {metadata.fields.map(field => (
              <div key={field.name}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {renderField(field)}
                {field.description && field.type !== 'checkbox' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {field.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Biztonsági figyelmeztetés */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
          <div className="flex gap-3">
            <div className="text-yellow-600 dark:text-yellow-400 text-xl">⚠️</div>
            <div className="text-sm text-yellow-700 dark:text-yellow-300">
              <strong>Biztonság:</strong> Az érzékeny adatok (jelszavak, API kulcsok) titkosítva kerülnek tárolásra.
              Ne oszd meg a credentials-öket másokkal!
            </div>
          </div>
        </div>
      </div>

      {/* Akciók */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Mégse
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Mentés...' : (isEditing ? 'Frissítés' : 'Létrehozás')}
        </button>
      </div>
    </div>
  );
};

export default CredentialEditor;
